import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Id, toast, ToastContentProps } from 'react-toastify';
import { convertSecondsToTime } from '../../../../utils/timeFormat';
import {
  IToastContentProps,
  RemainingToastLayout,
} from './RemainingToastLayout';

const MIN_SECONDS = 300;
const EDGE = 60;

export const useRemaining = (
  secondsBalance: number | null,
  avatarReady: boolean,
) => {
  const { t } = useTranslation();
  const containerId = 'reminder-container';
  const toastId = useRef<Id | null>(null);
  const intervalId = useRef<NodeJS.Timeout | null>(null);

  const [isRunning, setIsRunning] = useState(false);
  const [initialRemaining, setInitialRemaining] = useState<number | null>(null);

  const startTimer = (initialTime: number) => {
    if (initialTime <= 0) return;

    setInitialRemaining(secondsBalance);
    showRemaining(initialTime);
    setIsRunning(true);

    intervalId.current = setInterval(() => {
      setInitialRemaining((remaining) => {
        if (remaining === null) return null;
        const newRemaining = remaining - 1;
        updateRemaining(newRemaining);
        return newRemaining;
      });
    }, 1000);
  };

  const showRemaining = (totalSeconds: number) => {
    const initialData = {
      remainingTime: convertSecondsToTime(totalSeconds),
      advice: totalSeconds < EDGE ? t('avatar.hurryUp') : t('avatar.extendTime'),
    };

    const className =
      totalSeconds < EDGE
        ? 'tc-mv-blur-red text-white'
        : 'tc-mv-blur text-white';

    toastId.current = toast(
      ({ data }: ToastContentProps<IToastContentProps>) => (
        <RemainingToastLayout data={data} />
      ),
      {
        data: initialData,
        position: 'top-right',
        autoClose: false,
        hideProgressBar: true,
        closeButton: false,
        closeOnClick: false,
        icon: false,
        containerId,
        className,
      },
    );
  };

  const updateRemaining = (time: number) => {
    if (time <= 0) {
      clear();
      return;
    }

    const className =
      time < EDGE ? 'tc-mv-blur-red text-white' : 'tc-mv-blur text-white';
    const advice = time < EDGE ? t('avatar.hurryUp') : t('avatar.extendTime');

    if (toastId.current) {
      toast.update(toastId.current, {
        containerId,
        className,
        data: {
          remainingTime: convertSecondsToTime(time),
          advice,
        },
      });
    }
  };

  const hideRemaining = () => {
    if (toastId.current) {
      toast.dismiss(toastId.current);
      toastId.current = null;
    }
  };

  const clear = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
    hideRemaining();
    setInitialRemaining(null);
  };

  useEffect(() => {
    return () => {
      clear();
      setIsRunning(false);
    };
  }, []);

  useEffect(() => {
    if (!avatarReady || secondsBalance === null) {
      clear();
      return;
    }
    if (secondsBalance <= MIN_SECONDS && initialRemaining === null) {
      startTimer(secondsBalance);
    }
    if (secondsBalance > MIN_SECONDS) {
      setInitialRemaining(null);
      hideRemaining();
    }
    if (secondsBalance === 0) {
      clear();
    }
  }, [secondsBalance, avatarReady]);

  return { containerId, isRunning };
};
